/**
 * Category Service
 * 
 * Service for managing categories using Supabase client
 * Following the service pattern from accounts feature
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import { isAdmin } from '../utils/admin-utils';
import { 
  Category, 
  CreateCategoryInput, 
  UpdateCategoryInput,
  CategoryListParams,
  CategoryListResponse,
  CategoryType 
} from '../types/category.types';

export interface CategoryServiceResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// Helper function to convert database row to Category
function convertDbRowToCategory(row: any): Category {
  return {
    id: row.id,
    userId: row.user_id,
    parentId: row.parent_id || undefined,
    name: row.name,
    type: row.type as CategoryType,
    color: row.color || undefined,
    icon: row.icon || undefined,
    excludeFromSpending: row.exclude_from_spending || false,
    isSystem: row.is_system || false,
    gstApplicable: row.gst_applicable || false,
    deductible: row.deductible || false,
    slug: row.slug || undefined,
    note: row.note || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

// Helper function to convert Category to database insert format
function convertCategoryToDbInsert(input: CreateCategoryInput, userId: string): any {
  return {
    user_id: userId,
    parent_id: input.parentId || null,
    name: input.name,
    type: input.type,
    color: input.color || null,
    icon: input.icon || null,
    exclude_from_spending: input.excludeFromSpending ?? false,
    is_system: false, // User-created categories are never system categories
    gst_applicable: input.gstApplicable || false,
    deductible: input.deductible || false,
    note: input.note || null
  };
}

class CategoryService {
  private async propagateColorToDescendants(
    categoryId: string,
    color: string | null,
    userId: string,
    userIsAdmin: boolean
  ): Promise<void> {
    const { data: childRows, error } = await supabase
      .from('categories')
      .select('id, is_system, user_id')
      .eq('parent_id', categoryId)
      .order('name', { ascending: true });

    if (error || !childRows || childRows.length === 0) {
      return;
    }

    for (const child of childRows) {
      if (child.is_system && !userIsAdmin) {
        // Skip system categories if user isn't admin
        continue;
      }

      let childUpdate = supabase
        .from('categories')
        .update({ color })
        .eq('id', child.id);

      if (!child.is_system || !userIsAdmin) {
        childUpdate = childUpdate.eq('user_id', userId);
      }

      await childUpdate;

      await this.propagateColorToDescendants(child.id, color, userId, userIsAdmin);
    }
  }

  /**
   * List all categories for the current user
   */
  async listCategories(params?: CategoryListParams): Promise<CategoryServiceResponse<CategoryListResponse>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Apply filters
      // Handle multiple type filters by fetching separately and combining
      // This avoids issues with .in() chaining after .or()
      if (params?.type && Array.isArray(params.type) && params.type.length > 1) {
        // Multiple types - fetch each type separately and combine results
        const typeValues = params.type.map(t => String(t));
        const fetchPromises = typeValues.map(type => {
          let typeQuery = supabase
            .from('categories')
            .select('*', { count: 'exact' })
            .or(`user_id.eq.${userId},is_system.eq.true`)
            .eq('type', type);
          
          // Apply other filters
          if (params?.parentId !== undefined) {
            if (params.parentId === null) {
              typeQuery = typeQuery.is('parent_id', null);
            } else {
              typeQuery = typeQuery.eq('parent_id', params.parentId);
            }
          }
          if (params?.includeSystem === false) {
            typeQuery = typeQuery.eq('is_system', false);
          }
          if (params?.excludeFromSpending !== undefined) {
            typeQuery = typeQuery.eq('exclude_from_spending', params.excludeFromSpending);
          }
          if (params?.search) {
            typeQuery = typeQuery.or(`name.ilike.%${params.search}%,note.ilike.%${params.search}%`);
          }
          return typeQuery.order('name', { ascending: true });
        });

        let results;
        try {
          results = await Promise.all(fetchPromises);
        } catch (error: any) {
          console.error('Category service - Promise.all error:', error);
          return {
            error: {
              code: 'QUERY_ERROR',
              message: `Failed to fetch categories: ${error?.message || 'Unknown error'}`,
              details: error
            }
          };
        }

        // Check for errors
        const errors = results?.filter(r => r.error) || [];
        if (errors.length > 0) {
          const firstError = errors[0].error!;
          console.error('Category service query error:', firstError);
          return {
            error: {
              code: 'QUERY_ERROR',
              message: `Failed to fetch categories: ${firstError.message}`,
              details: firstError
            }
          };
        }

        // Combine results and remove duplicates (by id)
        const allCategories = results.flatMap(r => r.data || []);
        const categoryMap = new Map<string, any>();
        allCategories.forEach(cat => {
          if (!categoryMap.has(cat.id)) {
            categoryMap.set(cat.id, cat);
          }
        });
        const uniqueCategories = Array.from(categoryMap.values());
        const totalCount = uniqueCategories.length;

        const categories = uniqueCategories.map(convertDbRowToCategory);

        return {
          data: {
            categories,
            total: totalCount
          }
        };
      }

      // Single type or no type filter - use standard query
      let query = supabase
        .from('categories')
        .select('*', { count: 'exact' })
        .or(`user_id.eq.${userId},is_system.eq.true`);

      if (params?.type) {
        if (Array.isArray(params.type)) {
          // Single item array
          if (params.type.length === 0) {
            return {
              data: {
                categories: [],
                total: 0
              }
            };
          }
          query = query.eq('type', params.type[0]);
        } else {
          query = query.eq('type', params.type);
        }
      }

      if (params?.parentId !== undefined) {
        if (params.parentId === null) {
          query = query.is('parent_id', null);
        } else {
          query = query.eq('parent_id', params.parentId);
        }
      }

      if (params?.includeSystem === false) {
        query = query.eq('is_system', false);
      }

      if (params?.excludeFromSpending !== undefined) {
        query = query.eq('exclude_from_spending', params.excludeFromSpending);
      }

      if (params?.search) {
        query = query.or(`name.ilike.%${params.search}%,note.ilike.%${params.search}%`);
      }

      // Order by name by default
      query = query.order('name', { ascending: true });

      const { data, error, count } = await query;

      if (error) {
        console.error('Category service query error:', error);
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch categories: ${error.message}`,
            details: error
          }
        };
      }

      const categories = (data || []).map(convertDbRowToCategory);

      return {
        data: {
          categories,
          total: count || 0
        }
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Get a category by ID
   */
  async getCategoryById(id: string): Promise<CategoryServiceResponse<Category>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', id)
        .or(`user_id.eq.${userId},is_system.eq.true`)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found',
              details: error
            }
          };
        }
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch category: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: convertDbRowToCategory(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Create a new category
   */
  async createCategory(input: CreateCategoryInput): Promise<CategoryServiceResponse<Category>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Validate input
      if (!input.name || input.name.trim().length === 0) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Category name is required',
            details: 'Name cannot be empty'
          }
        };
      }

      if (input.name.length > 100) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Category name must be 100 characters or less',
            details: `Name length: ${input.name.length}`
          }
        };
      }

      if (!input.type || (input.type !== CategoryType.INCOME && input.type !== CategoryType.EXPENSE && input.type !== CategoryType.TRANSFER)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Category type must be "income", "expense", or "transfer"',
            details: `Invalid type: ${input.type}`
          }
        };
      }

      // Check for duplicate name (case-insensitive)
      const existing = await supabase
        .from('categories')
        .select('id')
        .eq('user_id', userId)
        .ilike('name', input.name)
        .single();

      if (existing.data) {
        return {
          error: {
            code: 'DUPLICATE_ERROR',
            message: 'Category name already exists',
            details: 'A category with this name already exists for your account'
          }
        };
      }

      // Validate parent category if provided
      if (input.parentId) {
        const parent = await this.getCategoryById(input.parentId);
        if (parent.error || !parent.data) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Parent category not found',
              details: 'The specified parent category does not exist'
            }
          };
        }

        if (parent.data.type !== input.type) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Parent category must be the same type',
              details: `Parent is ${parent.data.type}, but new category is ${input.type}`
            }
          };
        }
      }

      const insertData = convertCategoryToDbInsert(input, userId);

      const { data, error } = await supabase
        .from('categories')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'INSERT_ERROR',
            message: `Failed to create category: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: convertDbRowToCategory(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Update an existing category
   */
  async updateCategory(id: string, input: UpdateCategoryInput): Promise<CategoryServiceResponse<Category>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Check if category exists and belongs to user
      const existing = await this.getCategoryById(id);
      if (existing.error || !existing.data) {
        return existing;
      }

      // System categories cannot be updated unless user is admin
      let userIsAdmin = false;
      if (existing.data.isSystem) {
        userIsAdmin = await isAdmin();
        if (!userIsAdmin) {
          return {
            error: {
              code: 'PERMISSION_ERROR',
              message: 'System categories cannot be modified',
              details: 'This is a system category and cannot be edited'
            }
          };
        }
      }

      // Validate name if provided
      if (input.name !== undefined) {
        if (input.name.trim().length === 0) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Category name cannot be empty',
              details: 'Name is required'
            }
          };
        }

        if (input.name.length > 100) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Category name must be 100 characters or less',
              details: `Name length: ${input.name.length}`
            }
          };
        }

        // Check for duplicate name (excluding current category)
        const duplicate = await supabase
          .from('categories')
          .select('id')
          .eq('user_id', userId)
          .ilike('name', input.name)
          .neq('id', id)
          .single();

        if (duplicate.data) {
          return {
            error: {
              code: 'DUPLICATE_ERROR',
              message: 'Category name already exists',
              details: 'Another category with this name already exists'
            }
          };
        }
      }

      // Validate type if provided
      if (input.type !== undefined) {
        if (input.type !== CategoryType.INCOME && input.type !== CategoryType.EXPENSE && input.type !== CategoryType.TRANSFER) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Category type must be "income", "expense", or "transfer"',
              details: `Invalid type: ${input.type}`
            }
          };
        }

        // Validate parent category if type is being changed
        if (input.type !== existing.data.type && input.parentId !== undefined) {
          if (input.parentId) {
            const parent = await this.getCategoryById(input.parentId);
            if (parent.error || !parent.data) {
              return {
                error: {
                  code: 'VALIDATION_ERROR',
                  message: 'Parent category not found',
                  details: 'The specified parent category does not exist'
                }
              };
            }
            if (parent.data.type !== input.type) {
              return {
                error: {
                  code: 'VALIDATION_ERROR',
                  message: 'Parent category must be the same type',
                  details: `Parent is ${parent.data.type}, but new type is ${input.type}`
                }
              };
            }
          }
        }

        // Check for child categories when type is being changed
        if (input.type !== existing.data.type) {
          const { data: childCategories, error: childError } = await supabase
            .from('categories')
            .select('id')
            .eq('parent_id', id);

          if (childError) {
            return {
              error: {
                code: 'UNKNOWN_ERROR',
                message: 'Failed to check child categories',
                details: childError.message
              }
            };
          }

          if (childCategories && childCategories.length > 0) {
            return {
              error: {
                code: 'VALIDATION_ERROR',
                message: 'Cannot change category type while it has child categories',
                details: `This category has ${childCategories.length} child categor${childCategories.length === 1 ? 'y' : 'ies'}`
              }
            };
          }
        }
      }

      // Build update object
      const updateData: any = {};
      if (input.name !== undefined) updateData.name = input.name;
      if (input.type !== undefined) updateData.type = input.type;
      if (input.parentId !== undefined) updateData.parent_id = input.parentId || null;
      if (input.color !== undefined) updateData.color = input.color || null;
      if (input.icon !== undefined) updateData.icon = input.icon || null;
      if (input.excludeFromSpending !== undefined) updateData.exclude_from_spending = input.excludeFromSpending;
      if (input.gstApplicable !== undefined) updateData.gst_applicable = input.gstApplicable;
      if (input.deductible !== undefined) updateData.deductible = input.deductible;
      if (input.note !== undefined) updateData.note = input.note || null;

      // Build update query
      // For regular (non-system) categories, always filter by user_id
      // For system categories, admins can update without user_id filter (handled by RLS policy)
      let updateQuery = supabase
        .from('categories')
        .update(updateData)
        .eq('id', id);
      
      // For non-system categories, always filter by user_id
      // For system categories, check if user is admin before deciding whether to filter
      if (!existing.data.isSystem) {
        // Regular category - always filter by user_id
        updateQuery = updateQuery.eq('user_id', userId);
      } else {
        // System category - check if user is admin
        if (!userIsAdmin) {
          // Non-admin trying to update system category - this should be blocked by RLS
          // But we'll still filter by user_id to be safe
          updateQuery = updateQuery.eq('user_id', userId);
        }
        // If admin, don't filter by user_id - let RLS policy handle it
      }

      // Execute update and return updated row
      const { data, error } = await updateQuery.select('*').single();

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to update category: ${error.message}`,
            details: error
          }
        };
      }

      const updatedCategory = convertDbRowToCategory(data);

      if (
        input.color !== undefined &&
        input.color !== existing.data.color
      ) {
        await this.propagateColorToDescendants(
          updatedCategory.id,
          input.color || null,
          userId,
          userIsAdmin
        );
      }

      return {
        data: convertDbRowToCategory(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Delete a category
   */
  async deleteCategory(id: string): Promise<CategoryServiceResponse<void>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Check if category exists and belongs to user
      const existing = await this.getCategoryById(id);
      if (existing.error || !existing.data) {
        return {
          error: existing.error,
          data: undefined
        };
      }

      // System categories cannot be deleted unless user is admin
      if (existing.data.isSystem) {
        const userIsAdmin = await isAdmin();
        if (!userIsAdmin) {
          return {
            error: {
              code: 'PERMISSION_ERROR',
              message: 'System categories cannot be deleted',
              details: 'This is a system category and cannot be removed'
            }
          };
        }
      }

      // Block delete when category is used by active budgets (FR-032)
      const { budgetService } = await import('./budget-service');
      const activeBudgets = await budgetService.getActiveBudgetsUsingCategory(id, userId);
      if (activeBudgets.data && activeBudgets.data.length > 0) {
        return {
          error: {
            code: 'CATEGORY_IN_USE',
            message: `Cannot delete category — used by active budgets: ${activeBudgets.data.join(', ')}`,
            details: { budgetNames: activeBudgets.data },
          },
        };
      }

      // For admins, allow deleting system categories
      const userIsAdmin = existing.data.isSystem ? await isAdmin() : false;
      let query = supabase
        .from('categories')
        .delete()
        .eq('id', id);
      
      if (!userIsAdmin || !existing.data.isSystem) {
        // Regular users can only delete their own categories
        query = query.eq('user_id', userId);
      }

      const { error } = await query;

      if (error) {
        return {
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to delete category: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: undefined
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Bulk delete categories
   */
  async bulkDeleteCategories(ids: string[]): Promise<CategoryServiceResponse<void>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Verify all categories belong to user and are not system categories (unless admin)
      const userIsAdmin = await isAdmin();
      for (const id of ids) {
        const category = await this.getCategoryById(id);
        if (category.error || !category.data) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: `Category ${id} not found or not accessible`,
              details: category.error
            }
          };
        }
        if (category.data.isSystem && !userIsAdmin) {
          return {
            error: {
              code: 'PERMISSION_ERROR',
              message: 'Cannot delete system categories',
              details: `Category ${category.data.name} is a system category`
            }
          };
        }
      }

      // For admins, allow deleting system categories
      let query = supabase
        .from('categories')
        .delete()
        .in('id', ids);
      
      if (!userIsAdmin) {
        // Regular users can only delete their own categories
        query = query.eq('user_id', userId);
      }

      const { error } = await query;

      if (error) {
        return {
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to delete categories: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: undefined
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Bulk update categories
   */
  async bulkUpdateCategories(ids: string[], updates: Partial<Category>): Promise<CategoryServiceResponse<Category[]>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Build update object (only allow certain fields for bulk update)
      const updateData: any = {};
      if (updates.gstApplicable !== undefined) updateData.gst_applicable = updates.gstApplicable;
      if (updates.deductible !== undefined) updateData.deductible = updates.deductible;

      // For admins, allow updating system categories
      const userIsAdmin = await isAdmin();
      let query = supabase
        .from('categories')
        .update(updateData)
        .in('id', ids);
      
      if (userIsAdmin) {
        // Admin can update both user and system categories
        query = query.select() as any;
      } else {
        // Regular users can only update their own non-system categories
        query = query.eq('user_id', userId).eq('is_system', false).select() as any;
      }

      const { data, error } = await query;

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to update categories: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: (data || []).map(convertDbRowToCategory)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Bulk move categories to a different parent
   */
  async bulkMoveCategories(ids: string[], parentId: string | null): Promise<CategoryServiceResponse<Category[]>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Validate parent category if provided
      if (parentId) {
        const parent = await this.getCategoryById(parentId);
        if (parent.error || !parent.data) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Parent category not found',
              details: 'The specified parent category does not exist'
            }
          };
        }
      }

      // For admins, allow moving system categories
      const userIsAdmin = await isAdmin();
      let query = supabase
        .from('categories')
        .update({ parent_id: parentId })
        .in('id', ids);
      
      if (userIsAdmin) {
        // Admin can move both user and system categories
        query = query.select() as any;
      } else {
        // Regular users can only move their own non-system categories
        query = query.eq('user_id', userId).eq('is_system', false).select() as any;
      }

      const { data, error } = await query;

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to move categories: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: (data || []).map(convertDbRowToCategory)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }
}

export const categoryService = new CategoryService();

